import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useToast } from './toast';
import { internalFetch, internalSend } from '../lib/api';
import { Callout } from '@/components/ui/alert';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHead } from '@/components/ui/card';
import { CodeBlock } from '@/components/ui/code';
import { KvList, KvRow } from '@/components/ui/kv';

/** One-word badge labels (design system: a status is one word). */
const MANDATE_LABELS: Record<string, string> = {
  none: 'Aucun',
  active: 'Actif',
  inactive: 'Inactif',
};

const MANDATE_BADGES: Record<string, BadgeVariant> = {
  none: 'neutral',
  active: 'success',
  inactive: 'warning',
};

/** Stripe customer and SEPA mandate of a client: permanent mandate link, Stripe resync. */
export function ClientMandate({ clientId }: { clientId: string }): JSX.Element {
  const qc = useQueryClient();
  const { notify } = useToast();
  const base = `/internal/clients/${clientId}`;

  const billing = useQuery({
    queryKey: ['client-billing', clientId],
    queryFn: () =>
      internalFetch<{
        client: {
          stripeCustomerId: string | null;
          sepaMandateStatus: string;
          billingDeferred: boolean;
        };
      }>(`${base}/billing`),
  });

  const [mandateUrl, setMandateUrl] = useState<string | null>(null);
  const createMandateLink = useMutation({
    mutationFn: () => internalSend<{ url: string }>('POST', `${base}/mandate-link`),
    onSuccess: (created) => {
      setMandateUrl(created.url);
    },
  });
  const resync = useMutation({
    mutationFn: () => internalSend('POST', `${base}/stripe-sync`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['client-billing', clientId] });
      notify('Stripe resynchronisé');
    },
  });

  const setDeferral = useMutation({
    mutationFn: (deferred: boolean) =>
      internalSend('PUT', `${base}/billing-deferral`, { deferred }),
    onSuccess: (_, deferred) => {
      void qc.invalidateQueries({ queryKey: ['client-billing', clientId] });
      void qc.invalidateQueries({ queryKey: ['billing'] });
      notify(deferred ? 'Facturation reportée' : 'Report levé');
    },
  });

  const status = billing.data?.client.sepaMandateStatus ?? 'none';
  const deferred = billing.data?.client.billingDeferred ?? false;

  return (
    <Card>
      <CardHead title="Mandat SEPA (Stripe)" description="Prélèvement mensuel de la facture." />
      <CardContent className="grid gap-4">
        <KvList>
          <KvRow label="Client Stripe" mono>
            {billing.data?.client.stripeCustomerId ?? '—'}
          </KvRow>
          <KvRow label="Mandat SEPA">
            <Badge variant={MANDATE_BADGES[status] ?? 'neutral'}>
              {MANDATE_LABELS[status] ?? status}
            </Badge>
          </KvRow>
          <KvRow label="Facturation">
            {deferred ? <Badge variant="warning">Reportée</Badge> : 'Mensuelle'}
          </KvRow>
        </KvList>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            onClick={() => {
              createMandateLink.mutate();
            }}
          >
            Générer le lien de mandat
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              resync.mutate();
            }}
          >
            Resynchroniser Stripe
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={billing.data === undefined || setDeferral.isPending}
            onClick={() => {
              setDeferral.mutate(!deferred);
            }}
          >
            {deferred ? 'Ne plus reporter la facturation' : 'Reporter la facturation'}
          </Button>
        </div>
        {deferred ? (
          <Callout variant="warning" title="Facturation reportée">
            Chaque clôture passe le mois au mois suivant, sans limite. Une fois le report levé, la
            clôture suivante facture tous les mois reportés sur une seule facture.
          </Callout>
        ) : null}
        {mandateUrl !== null ? (
          <Callout variant="info" title="Lien de mandat permanent (à transmettre au client)">
            <CodeBlock className="mt-2">{mandateUrl}</CodeBlock>
          </Callout>
        ) : null}
      </CardContent>
    </Card>
  );
}
